import { createSlice, Draft } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { Id, Wishlist, WishlistItem } from "../types";
import dayjs from "dayjs";

export interface WishlistState {
  wishlists: Wishlist[];
  isLoading: boolean;
}

type ServerData = Omit<WishlistState, "isLoading">;

const initialState: WishlistState = {
  wishlists: [],
  isLoading: true,
};

interface RemoveItemActionPayload {
  wishlistId: Id;
  itemId: Id;
}

interface UpdateItemActionPayload extends RemoveItemActionPayload {
  item: Omit<WishlistItem, "createdAt">;
}

function findWishlist(
  wishlists: Draft<Wishlist[]>,
  id: Id,
): Draft<Wishlist> | undefined {
  const ind = wishlists.findIndex((w) => w.id === id);
  if (ind === -1) {
    return undefined;
  }
  return wishlists[ind];
}

export const wishlistSlice = createSlice({
  name: "wishlists",
  initialState,
  reducers: {
    removeItem: (state, action: PayloadAction<RemoveItemActionPayload>) => {
      const wishlist = findWishlist(state.wishlists, action.payload.wishlistId);
      if (!wishlist) {
        return;
      }
      wishlist.items = wishlist.items.filter(
        (i) => i.id !== action.payload.itemId,
      );
    },
    updateItem: (state, action: PayloadAction<UpdateItemActionPayload>) => {
      const wishlist = findWishlist(state.wishlists, action.payload.wishlistId);
      if (wishlist === undefined) {
        return;
      }
      if (!wishlist.items) {
        wishlist.items = [];
      }
      const itemInd = wishlist.items.findIndex(
        (i) => i.id === action.payload.itemId,
      );
      if (-1 === itemInd) {
        wishlist.items.push({
          ...action.payload.item,
          createdAt: dayjs().toISOString(),
          id: action.payload.itemId,
        });
      } else {
        wishlist.items[itemInd] = {
          ...wishlist.items[itemInd],
          ...action.payload.item,
        };
      }
    },
    addWishlist: (state, action: PayloadAction<Wishlist>) => {
      state.wishlists.push(action.payload);
    },
    removeWishlist: (state, action: PayloadAction<Id>) => {
      state.wishlists = state.wishlists.filter((w) => w.id !== action.payload);
    },
    loadData: (state, action: PayloadAction<ServerData>) => {
      state.isLoading = false;
      const newWishlistIds = action.payload.wishlists.map((w) => w.id);
      state.wishlists = [
        ...state.wishlists.filter((w) => newWishlistIds.indexOf(w.id) == -1),
        ...action.payload.wishlists,
      ];
    },
    setIsLoading: (state) => {
      state.isLoading = true;
    }
  },
});

export const { removeItem, updateItem, addWishlist, removeWishlist, loadData, setIsLoading } =
  wishlistSlice.actions;

export default wishlistSlice.reducer;
